import { useContext, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Mail } from 'lucide-react'
import { toast } from 'sonner'

import { DashboardLayout } from '@/components/layouts/DashboardLayout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { OrdersSpaceTabs } from '@/components/features/suppliers/OrdersSpaceTabs'
import { usePermissions } from '@/hooks/usePermissions'
import { getMailboxes, getSuggestedMailboxAddress, removeMailbox, startMailboxConnect, type MailboxConnectRequest, type MailboxDto } from '@/lib/api/v2/planning'
import { ApiError } from '@/lib/api/client'
import {
  ADMIN_CONSENT_HELP_LINK_LABEL,
  MAILBOX_CONSENT_LABEL,
  MAILBOX_HOLDER_INFORMED_LABEL,
  needsHolderInformed,
  extractAadstsCode,
  rememberAttempt,
  takeAttempt,
} from '@/lib/mailboxes'
import { AdminConsentHelp } from '@/components/features/suppliers/AdminConsentHelp'
import { takePendingImport } from '@/lib/supplierImport'
import { AuthContext } from '@/lib/auth-context-instance'
import { PERMISSIONS } from '@/lib/permissions'

const errorMessage = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : fallback)

/** Asks the API for the Microsoft sign-in URL, then leaves for it. Rejects with the API's reason. */
async function goToMicrosoft(request: MailboxConnectRequest) {
  const { authorizeUrl } = await startMailboxConnect(request)
  // MS-05: a failed return from Microsoft retries the same thing (add that address, or reconnect that mailbox).
  rememberAttempt(request)
  window.location.assign(authorizeUrl)
}

/**
 * Back from Microsoft with the browser's Back button, the page can come back from the back/forward
 * cache as it was left — on its way to Microsoft. Nothing is in flight any more.
 */
function useResetOnBackForwardRestore(reset: () => void) {
  const latest = useRef(reset)
  useEffect(() => {
    latest.current = reset
  })
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) latest.current()
    }
    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  }, [])
}

/**
 * SOLY-SUPPLIER-002 MS-04 — the mailboxes Soly reads for supplier orders. One path for every case:
 * type the address, sign in at Microsoft (with that mailbox, or an account that opens it).
 * Colleagues see which mailboxes are followed, never their content.
 */
export default function MailboxesPage() {
  const { can, reasonIfDenied } = usePermissions()
  const canManage = can(PERMISSIONS.ORDERS_MANAGE)
  const canManageSettings = can(PERMISSIONS.SETTINGS_MANAGE)
  const manageHint = reasonIfDenied(PERMISSIONS.ORDERS_MANAGE)
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  // Read once at arrival: back from Microsoft (?connected=1, or ?retry=1 when it did not connect), or
  // sent here by the import (?add=1).
  const [arrival] = useState(() => {
    const failure = searchParams.get('retry') === '1' ? { attempt: takeAttempt(), code: extractAadstsCode(searchParams.get('code')) } : null
    const reconnectId = failure?.attempt && 'mailboxId' in failure.attempt ? failure.attempt.mailboxId : null
    return {
      connected: searchParams.get('connected') === '1',
      add: searchParams.get('add') === '1',
      // MS-05: what Microsoft did not complete — an addition (the address, possibly unknown) or a reconnection.
      addFailure: failure && !reconnectId ? { address: failure.attempt && 'address' in failure.attempt ? failure.attempt.address : '', code: failure.code } : null,
      reconnectFailure: failure && reconnectId ? { mailboxId: reconnectId, code: failure.code } : null,
    }
  })
  const [mailboxes, setMailboxes] = useState<MailboxDto[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState(() => (arrival.add || arrival.addFailure !== null) && canManage)
  const [reconnectFailure, setReconnectFailure] = useState(arrival.reconnectFailure)
  // The failed addition belongs to the dialog that reopened it: once closed, « Ajouter une boîte » starts fresh.
  const [addFailure, setAddFailure] = useState(arrival.addFailure)
  const [disconnecting, setDisconnecting] = useState<MailboxDto | null>(null)
  const [reconnecting, setReconnecting] = useState<string | null>(null)
  const latestRequest = useRef(0)

  const load = () => {
    const request = ++latestRequest.current
    return getMailboxes()
      .then((list) => {
        if (request === latestRequest.current) {
          setMailboxes(list)
          setError(null)
        }
      })
      .catch((e) => {
        if (request === latestRequest.current) setError(errorMessage(e, 'Les boîtes n’ont pas pu être chargées.'))
      })
  }

  // Set when the page hands over to the import: nothing else must navigate after that.
  const leaving = useRef(false)

  useEffect(() => {
    // SOLY-SUPPLIER-002 MS-03 — the import asked before any mailbox existed starts once one is connected.
    if (arrival.connected && takePendingImport()) {
      leaving.current = true
      navigate('/supplier-orders/suppliers?import=1')
      return
    }
    if (!leaving.current) void load()
    // Once, at arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (leaving.current) return
    if (searchParams.get('connected') || searchParams.get('error') || searchParams.get('add') || searchParams.get('retry')) {
      const message = searchParams.get('error')
      // An id, so the double effect run of development StrictMode shows it once.
      if (message) toast.error(message, { id: 'mailbox-connect-error' })
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, setSearchParams])

  useResetOnBackForwardRestore(() => setReconnecting(null))

  const reconnect = async (m: MailboxDto) => {
    // A reconnection is not the mailbox an import was waiting for.
    takePendingImport()
    setReconnecting(m.id)
    try {
      await goToMicrosoft({ mailboxId: m.id })
    } catch (e) {
      toast.error(errorMessage(e, 'La reconnexion n’a pas pu démarrer.'))
      setReconnecting(null)
    }
  }

  // Giving up the addition also gives up the import that waited for it.
  const closeAdding = () => {
    takePendingImport()
    setAddFailure(null)
    setAdding(false)
  }

  const addButton = (testId: string) => (
    <Button onClick={() => setAdding(true)} disabled={!canManage} title={manageHint} data-testid={testId}>
      Ajouter une boîte
    </Button>
  )

  return (
    <DashboardLayout>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Mail className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold">Boîtes mail</h1>
          </div>
          {mailboxes !== null && mailboxes.length > 0 && addButton('mailboxes-add-btn')}
        </div>

        <OrdersSpaceTabs active="mailboxes" />

        {error && (
          <div className="flex items-center gap-3 rounded-md border border-destructive/40 p-3 text-sm text-destructive">
            {error}
            <Button size="sm" variant="outline" onClick={() => void load()} data-testid="mailboxes-retry-btn">
              Réessayer
            </Button>
          </div>
        )}

        {mailboxes === null && !error && <Skeleton className="h-32 w-full" />}

        {mailboxes !== null && mailboxes.length === 0 && (
          <div className="space-y-3 rounded-md border p-8 text-center text-sm text-muted-foreground" data-testid="mailboxes-empty">
            <p>Aucune boîte mail.</p>
            {addButton('mailboxes-empty-add-btn')}
          </div>
        )}

        {mailboxes !== null && mailboxes.length > 0 && (
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Boîte</TableHead>
                  <TableHead>Connectée avec</TableHead>
                  <TableHead>Ajoutée par</TableHead>
                  <TableHead>État</TableHead>
                  <TableHead className="w-0" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {mailboxes.map((m) => (
                  <TableRow key={m.id} data-testid="mailboxes-row">
                    <TableCell className="font-mono text-xs">{m.address}</TableCell>
                    <TableCell className="font-mono text-xs">{m.accountEmail}</TableCell>
                    <TableCell className="text-sm">{m.isMine ? `${m.declarantName} (vous)` : m.declarantName}</TableCell>
                    <TableCell>
                      {m.status === 'Active' ? (
                        <Badge variant="outline" className="border-green-300 bg-green-50 font-normal text-green-800">Active</Badge>
                      ) : (
                        <Badge variant="outline" className="border-destructive font-normal text-destructive">À reconnecter</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-2">
                        {m.status === 'NeedsReconnect' && m.isMine && (
                          <Button
                            size="sm"
                            onClick={() => void reconnect(m)}
                            disabled={!canManage || reconnecting !== null}
                            title={manageHint}
                            data-testid="mailboxes-row-reconnect-btn"
                          >
                            Reconnecter
                          </Button>
                        )}
                        {(m.isMine || canManageSettings) && (
                          <Button size="sm" variant="outline" onClick={() => setDisconnecting(m)} data-testid="mailboxes-row-disconnect-btn">
                            Déconnecter
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog open={adding} onOpenChange={(open) => (open ? setAdding(true) : closeAdding())}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="mailboxes-add-dialog">
          {adding && <AddMailboxForm onCancel={closeAdding} failure={addFailure} listLoaded={mailboxes !== null} />}
        </DialogContent>
      </Dialog>

      <ReconnectFailedDialog
        failure={reconnectFailure}
        mailboxes={mailboxes}
        busy={reconnecting !== null}
        onRetry={(m) => void reconnect(m)}
        onClose={() => setReconnectFailure(null)}
      />

      <Dialog open={disconnecting !== null} onOpenChange={(o) => !o && setDisconnecting(null)}>
        <DialogContent data-testid="mailboxes-disconnect-dialog">
          <DialogHeader>
            <DialogTitle>Déconnecter {disconnecting?.address} ?</DialogTitle>
            <DialogDescription>Soly ne lira plus cette boîte. Les fournisseurs déjà ajoutés restent.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisconnecting(null)} data-testid="mailboxes-disconnect-cancel-btn">
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                if (!disconnecting) return
                try {
                  await removeMailbox(disconnecting.id)
                  setDisconnecting(null)
                  void load()
                } catch (e) {
                  toast.error(errorMessage(e, 'La boîte n’a pas pu être déconnectée.'))
                }
              }}
              data-testid="mailboxes-disconnect-confirm-btn"
            >
              Déconnecter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  )
}

/**
 * MS-05 — a reconnection Microsoft did not complete: offered again, with the message for the
 * administrator. Opens once the list is loaded (it names the mailbox).
 */
function ReconnectFailedDialog({
  failure,
  mailboxes,
  busy,
  onRetry,
  onClose,
}: {
  failure: { mailboxId: string; code: string | null } | null
  mailboxes: MailboxDto[] | null
  busy: boolean
  onRetry: (m: MailboxDto) => void
  onClose: () => void
}) {
  const target = mailboxes?.find((m) => m.id === failure?.mailboxId)
  return (
    <Dialog open={failure !== null && target !== undefined} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="mailboxes-reconnect-failed-dialog">
        <DialogHeader>
          <DialogTitle>Reconnecter {target?.address}</DialogTitle>
        </DialogHeader>
        {target && <AdminConsentHelp address={target.address} failed code={failure?.code ?? null} onRetry={() => onRetry(target)} retryDisabled={busy} />}
      </DialogContent>
    </Dialog>
  )
}

/**
 * « Ajouter une boîte » — the address, the consent (the checkbox label is the consent itself), and a
 * second checkbox when the mailbox is someone else's. Nothing is followed before Microsoft says yes.
 */
function AddMailboxForm({
  onCancel,
  failure = null,
  listLoaded,
}: {
  onCancel: () => void
  failure?: { address: string; code: string | null } | null
  /** Nothing is proposed before the list of followed mailboxes is on screen (AC « Rien n'est proposé »). lang:fr-ok */
  listLoaded: boolean
}) {
  const userEmail = useContext(AuthContext)?.user?.email
  const [address, setAddress] = useState(failure?.address ?? '')
  const [consented, setConsented] = useState(false)
  const [informed, setInformed] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [helpOpen, setHelpOpen] = useState(failure !== null)
  // Once there is an address (typed, or the one Microsoft did not connect), a late suggestion never replaces it.
  const touched = useRef(Boolean(failure?.address))

  useResetOnBackForwardRestore(() => setBusy(false))

  // MS-05: start with the person's own address when it is not followed yet (the API decides: not a
  // public provider, not already followed in the organization).
  useEffect(() => {
    if (touched.current || !listLoaded) return
    let current = true
    getSuggestedMailboxAddress()
      .then(({ address: suggested }) => {
        if (current && suggested && !touched.current) {
          setAddress(suggested)
          setConsented(false)
          setInformed(false)
        }
      })
      .catch(() => {
        // No suggestion: the field stays empty, as before.
      })
    return () => {
      current = false
    }
  }, [listLoaded])

  const askInformed = needsHolderInformed(address, userEmail)
  const ready = address.trim() !== '' && consented && (!askInformed || informed)

  const submit = async () => {
    if (!ready || busy) return
    setBusy(true)
    setError(null)
    try {
      await goToMicrosoft({ address: address.trim() })
    } catch (e) {
      setError(errorMessage(e, 'La connexion à Microsoft n’a pas pu démarrer.'))
      setBusy(false)
    }
  }

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault()
        void submit()
      }}
    >
      <DialogHeader>
        <DialogTitle>Ajouter une boîte mail</DialogTitle>
      </DialogHeader>

      <div className="space-y-1">
        <Label htmlFor="mailbox-address">Adresse</Label>
        <Input
          id="mailbox-address"
          inputMode="email"
          autoComplete="off"
          value={address}
          onChange={(e) => {
            touched.current = true
            setAddress(e.target.value)
            setError(null)
            // A consent is given for one mailbox: another address asks again.
            setConsented(false)
            setInformed(false)
          }}
          placeholder="info@entreprise.be"
          disabled={busy}
          data-testid="mailboxes-add-address-input"
        />
      </div>

      <div className="flex items-start gap-2">
        <Checkbox
          id="mailbox-consent"
          checked={consented}
          onCheckedChange={(v) => setConsented(v === true)}
          disabled={busy}
          className="mt-0.5"
          data-testid="mailboxes-add-consent-checkbox"
        />
        <label htmlFor="mailbox-consent" className="cursor-pointer text-sm leading-snug">
          {MAILBOX_CONSENT_LABEL}
        </label>
      </div>

      {askInformed && (
        <div className="flex items-start gap-2">
          <Checkbox
            id="mailbox-informed"
            checked={informed}
            onCheckedChange={(v) => setInformed(v === true)}
            disabled={busy}
            className="mt-0.5"
            data-testid="mailboxes-add-informed-checkbox"
          />
          <label htmlFor="mailbox-informed" className="cursor-pointer text-sm leading-snug">
            {MAILBOX_HOLDER_INFORMED_LABEL}
          </label>
        </div>
      )}

      {helpOpen ? (
        <AdminConsentHelp
          address={address}
          failed={failure !== null}
          code={failure?.code ?? null}
          onRetry={() => void submit()}
          retryDisabled={!ready || busy}
        />
      ) : (
        <button
          type="button"
          className="text-left text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground"
          onClick={() => setHelpOpen(true)}
          data-testid="mailboxes-admin-help-toggle"
        >
          {ADMIN_CONSENT_HELP_LINK_LABEL}
        </button>
      )}

      {error && (
        <p className="text-sm text-destructive" role="alert" data-testid="mailboxes-add-error">
          {error}
        </p>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} data-testid="mailboxes-add-cancel-btn">
          Annuler
        </Button>
        <Button type="submit" disabled={!ready || busy} data-testid="mailboxes-add-continue-btn">
          Continuer vers Microsoft
        </Button>
      </DialogFooter>
    </form>
  )
}
